// @ts-strict-ignore
import type {
  TabItem,
  WizardNavigationItem,
} from '@usealloy/component-library';
import {
  Banner,
  Button,
  ButtonVariant,
  DataViewer,
  DataViewerType,
  Modal,
  Spinner,
  Tabs,
  Text,
  WizardNavigation,
  Box,
} from '@usealloy/component-library';
import React from 'react';
import type { APIData, AxiosData, ServiceResponseData } from '../types';
import { useGetVendorAPICalls } from '~/entities/hooks';
import { NoDataText } from '~/entities/evaluations/service/styles';
import { isEmpty } from '~/helpers/formatting';
import { getMatchedErrorAttributes } from '../helpers';
import { useAppSelector } from '~/store/reduxHooks';
import { selectUniversalAttributes } from 'flow/store/redux/selectors';
import type { Attribute } from 'flow/store/engine/types';

interface Props {
  title: string;
  isOpen: boolean;
  closeModal: () => void;
  evaluationToken?: string;
  actionId?: number | string;
  actionPublicId?: string;
  workflowPublicVersion?: number;
  formattedResponse?: {};
  rawResponse?: {};
  typhonDomain: string;
}
export const ServiceResponseModalNew = ({
  title,
  isOpen,
  closeModal,
  evaluationToken,
  actionId,
  actionPublicId,
  workflowPublicVersion,
  formattedResponse,
  rawResponse,
  typhonDomain,
}: Props) => {
  const closeButton = <Button variant={ButtonVariant.primary} onClick={closeModal}>Close</Button>;
  // in v4, we have to use the isInitialLoading flag for disabled queries
  const { data, isInitialLoading } = useGetVendorAPICalls(
    evaluationToken,
    actionId,
    typhonDomain,
    actionPublicId,
    workflowPublicVersion,
  );
  const universalAttributes = useAppSelector(selectUniversalAttributes);

  const vendorApiCalls = data?.data?.vendor_api_calls ?? [];
  let serviceResponse = data?.data?.service_response;

  if (isEmpty(serviceResponse?.raw) && !isEmpty(rawResponse)) {
    serviceResponse = {
      ...serviceResponse,
      raw: rawResponse,
    };
  }

  if (isEmpty(serviceResponse?.formatted) && !isEmpty(formattedResponse)) {
    serviceResponse = {
      ...serviceResponse,
      formatted: formattedResponse,
    };
  }

  const matchedErrorAttributes = getMatchedErrorAttributes(
    serviceResponse?.formatted?.error_attributes,
    universalAttributes,
  );
  const hasErrorAttributes = matchedErrorAttributes.length > 0;

  const callTabs: WizardNavigationItem[] = vendorApiCalls.map((vendorApiCall, index) => {
    const error = hasErrorAttributes ? '' : getError(vendorApiCall);
    const hasCallError = hasErrorAttributes || error !== '';
    return ({
      content: <InnerTab vendorApiCall={vendorApiCall} error={error} />,
      label: `Call ${index + 1}`,
      value: `Call ${index + 1}`,
      status: hasCallError ? 'error' : 'none',
    });
  });

  const compiledResponseTab: WizardNavigationItem[] = isEmpty(serviceResponse)
    ? []
    : [{
      content: <InnerTab serviceResponse={serviceResponse} />,
      label: 'Compiled Response',
      value: 'Compiled Response',
      status: hasErrorAttributes ? 'error' : 'none',
    }];

  const content = () => {
    if (isInitialLoading) return <Spinner size="large" />;
    if (
      isEmpty(vendorApiCalls)
      && isEmpty(serviceResponse?.formatted)
      && isEmpty(serviceResponse?.raw)
    ) {
      return (
        <NoDataText size={3}>
          No data found for this instance.
        </NoDataText>
      );
    }
    return (
      <WizardNavigation
        tabs={[...callTabs, ...compiledResponseTab]}
        numberedSteps={false}
      />
    );
  };


  return (
    <Modal
      header={title}
      isOpen={isOpen}
      onClose={closeModal}
      primaryAction={closeButton}
      size="largeWithTabs"
    >
      <ErrorAttributeBanners attributes={matchedErrorAttributes} />
      {content()}
    </Modal>
  );
};

function ErrorAttributeBanners({ attributes }: { attributes: Attribute[] }) {
  if (!attributes.length) return null;
  return (
    <Box direction="column" gap={2}>
      {attributes.map(attribute => (
        <Banner key={attribute.attribute_name} open outline variant="error">
          <Text lineHeight={3} weight={700}>{attribute.display_name}</Text>
          <Text>{attribute.description}</Text>
        </Banner>
      ))}
    </Box>
  );
}

function InnerTab(
  { vendorApiCall, serviceResponse, error }:
  { vendorApiCall?: APIData; serviceResponse?: ServiceResponseData; error?: string | null },
) {
  const isCompiledTab = serviceResponse !== undefined;
  let tabs: TabItem[] = [];
  if (!isCompiledTab) {
    tabs = [
      {
        content: <InnerTabContent
          info={vendorApiCall.request}
          error={error}
        />,
        label: 'Request',
        value: 'request',
      },
      {
        content: <InnerTabContent
          info={vendorApiCall.response}
        />,
        label: 'Response',
        value: 'response',
      },
    ];
  } else {
    if (!isEmpty(serviceResponse.raw)) {
      tabs = [{
        content: <InnerCompiledTabContent data={serviceResponse.raw} />,
        label: 'Raw Response',
        value: 'Raw Response',
      }];
    }
    if (!isEmpty(serviceResponse.formatted)) {
      tabs = [...tabs, {
        content: <InnerCompiledTabContent data={serviceResponse.formatted} />,
        label: 'Formatted Response',
        value: 'Formatted Response',
      }];
    }
  }

  return (
    <div style={{ overflow: 'auto' }}>
      <Tabs
        tabs={tabs}
      />
    </div>
  );
}

export function InnerTabContent(
  { info, error }:
  { info: APIData['request'] | APIData['response']; error?: string | null },
) {
  const { headers, data, ...parameters } = info;
  const isSoap = isSoapRequest(headers);

  return (
    <Box style={{ height: 'calc(100vh - 350px)', width: 610 }} direction="column" gap={2} justifyContent="flex-start">
      {error && (
        <Banner open outline variant="warning">
          <Text lineHeight={3}>
            {error}
          </Text>
        </Banner>
      )}
      {headers && (
        (
        <DataViewer
          header="Headers"
          data={headers}
          snackbarSuccessText="Copied"
          type={DataViewerType.json}
          initiallyExpand
        />
        )
      )}
      <DataViewer
        header="Parameters"
        data={parameters}
        snackbarSuccessText="Copied"
        type={DataViewerType.json}
        initiallyExpand
      />
      {data && !isSoap
        && (
          <DataViewer
            header="Body"
            data={data}
            snackbarSuccessText="Copied"
            type={DataViewerType.json}
            initiallyExpand
          />
        )}
      {data && isSoap && (
        <DataViewer
          header="Body"
          data={typeof data === 'string' ? data.replace(/\\/g, '') : ''}
          snackbarSuccessText="Copied"
          type={DataViewerType.xml}
          initiallyExpand
        />
      )}
    </Box>
  );
}

export function InnerCompiledTabContent({ data }: { data: {} }) {
  return (
    <div style={{ height: 'calc(100vh - 350px)', width: 610 }}>
      <DataViewer
        header="Body"
        data={data}
        snackbarSuccessText="Copied"
        type={DataViewerType.json}
        initiallyExpand
      />
    </div>
  );
}

function isSoapRequest(headers: APIData['response']['headers']) {
  return Boolean(headers?.['Content-Type']?.includes('xml') || headers?.['content-type']?.includes('xml'))
    || headers?.['Content-Type']?.includes('text/html') || headers?.['content-type']?.includes('text/html');
}

function getError(vendorApiCall: APIData) {
  const HTTPMultipleChoices = 300;
  const hasError = vendorApiCall.response.status_code >= HTTPMultipleChoices;
  if (!hasError) return '';
  const errorMsg = (vendorApiCall as AxiosData).response?.status_text;
  return `Error ${vendorApiCall.response.status_code}${isEmpty(errorMsg) ? '' : `: ${errorMsg}`}`;
}

export default ServiceResponseModalNew;
